/*
 * File: SharedActionContext.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 5:03 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 11:28 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

import React, {
	createContext, useReducer, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';

// Components
import { AuthenticateModal } from '../components/AuthenticateModal';
import { ReportModal } from '../components/ReportModal';
import { MessageCreatorModal } from '../components/MessageCreatorModal';
import { QuestionCreatorModal } from '../components/QuestionCreatorModal';


/**
 * Context
 */

const Context = createContext();


/**
 * Component
 */

const SharedActionContext = ({ children }) => {

	// Create state handlers
	const [messageCreatorModalOpen, setMessageCreatorModalOpen] = useState(false);
	const [questionCreatorModalOpen, setQuestionCreatorModalOpen] = useState(false);
	const [authenticateModalOpen, setAuthenticateModalOpen] = useState(false);
	const [reportModalOpen, setReportModalOpen] = useState(false);
	const [messageCreatorModalState, setMessageCreatorModalState] = useState({});
	const [questionCreatorModalState, setQuestionCreatorModalState] = useState({});
	const [authenticateModalState, setAuthenticateModalState] = useState({});
	const [reportModalState, setReportModalState] = useState({});

	// Open message creator modal function
	const openMessageCreatorModal = (state) => {
		setMessageCreatorModalOpen(true);
		setMessageCreatorModalState(state);
	};

	// Close message creator modal function
	const closeMessageCreatorModal = () => {
		setMessageCreatorModalOpen(false);
	};

	// Open question creator modal function
	const openQuestionCreatorModal = (state) => {
		setQuestionCreatorModalOpen(true);
		setQuestionCreatorModalState(state);
	};

	// Close question creator modal function
	const closeQuestionCreatorModal = () => {
		setQuestionCreatorModalOpen(false);
	};

	// Open authenticate modal function
	const openAuthenticateModal = (state) => {
		setAuthenticateModalOpen(true);
		setAuthenticateModalState(state);
	};

	// Close authenticate modal function
	const closeAuthenticateModal = () => {
		setAuthenticateModalOpen(false);
	};

	// Open report modal function
	const openReportModal = (state) => {
		setReportModalOpen(true);
		setReportModalState(state);
	};

	// Close report modal function
	const closeReportModal = () => {
		setReportModalOpen(false);
	};

	// Create reducer
	const reducer = (state, action) => {
		switch (action.type) {
			case 'OPEN_MESSAGE_CREATOR_MODAL':
				openMessageCreatorModal(action.payload);
				return { ...state };
			case 'OPEN_QUESTION_CREATOR_MODAL':
				openQuestionCreatorModal(action.payload);
				return { ...state };
			case 'OPEN_AUTHENTICATE_MODAL':
				openAuthenticateModal(action.payload);
				return { ...state };
			case 'OPEN_REPORT_MODAL':
				openReportModal(action.payload);
				return { ...state };
			default:
				return state;
		}
	};

	// Set reducers
	const [contextState, contextDispatch] = useReducer(reducer, {});

	// Create memoized context provider
	const contextProvider = useMemo(() => ({ state: contextState, dispatch: contextDispatch }), [contextState, contextDispatch]);

	// Render component
	return (
		<Context.Provider value={contextProvider}>

			{/* Children */}
			{children}

			{/* Modals */}
			<MessageCreatorModal
				isOpen={messageCreatorModalOpen}
				handleClose={closeMessageCreatorModal}
				data={messageCreatorModalState}
				postCreateActions={(messageObj) => {

					// NV - ADD TO FEEDS (pending)

				}}
			/>
			<QuestionCreatorModal
				isOpen={questionCreatorModalOpen}
				handleClose={closeQuestionCreatorModal}
				data={questionCreatorModalState}
				postCreateActions={(questionObj) => {

					// NV - ADD TO FEEDS (pending)

				}}
			/>
			<ReportModal
				isOpen={reportModalOpen}
				handleClose={closeReportModal}
				source={reportModalState.source}
				sourceId={reportModalState.sourceId}
				hideContent={() => {

					// NV

				}}
			/>
			<AuthenticateModal
				isOpen={authenticateModalOpen}
				handleClose={closeAuthenticateModal}
				data={authenticateModalState}
			/>

		</Context.Provider>
	);
};


/**
 * Configuration
 */

SharedActionContext.displayName = 'SharedActionContext';
SharedActionContext.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};
SharedActionContext.defaultProps = {
	children: null
};


/**
 * Exports
 */

export { SharedActionContext };
export default Context;
